import { z } from "zod";
import { valorDoServico, type ServicoComValor } from "./valor-do-pedido";

/**
 * Taxa de urgência que o laboratório configura uma vez e confirma no aceite do pedido urgente —
 * um valor fixo ou uma porcentagem dos serviços. A clínica continua vendo só o aviso de sempre
 * ("pode ser cobrada uma taxa extra"); o valor entra como acréscimo do pedido.
 */
export const TIPOS_DE_TAXA_DE_URGENCIA = ["FIXA", "PERCENTUAL"] as const;
export type TipoDeTaxaDeUrgencia = (typeof TIPOS_DE_TAXA_DE_URGENCIA)[number];
export type TaxaDeUrgencia = { tipo: TipoDeTaxaDeUrgencia; valor: number };

export const DESCRICAO_DA_TAXA_DE_URGENCIA = "Taxa de urgência";

/** Do perfil do laboratório; sem tipo ou valor válido, sem taxa. */
export function taxaDoPerfil(p: { taxaDeUrgenciaTipo?: string | null; taxaDeUrgenciaValor?: number | null } | null | undefined): TaxaDeUrgencia | null {
  const tipo = p?.taxaDeUrgenciaTipo;
  const valor = p?.taxaDeUrgenciaValor;
  if (!tipo || !(TIPOS_DE_TAXA_DE_URGENCIA as readonly string[]).includes(tipo) || !valor || valor < 1) return null;
  if (tipo === "PERCENTUAL" && valor > 100) return null;
  return { tipo: tipo as TipoDeTaxaDeUrgencia, valor };
}

/** Quanto a taxa dá neste pedido, em centavos. Porcentagem é sobre os serviços com preço (sem os
 *  acréscimos); pedido sem preço nenhum dá 0 — não há sobre o que calcular. */
export function valorDaTaxaDeUrgencia(taxa: TaxaDeUrgencia, servicos: ServicoComValor[]): number {
  if (taxa.tipo === "FIXA") return taxa.valor;
  const base = servicos.reduce((t, s) => t + (valorDoServico(s) ?? 0), 0);
  return Math.round((base * taxa.valor) / 100);
}

export const taxaDeUrgenciaSchema = z
  .object({
    tipo: z.enum(TIPOS_DE_TAXA_DE_URGENCIA).nullable(),
    /** FIXA: centavos. PERCENTUAL: porcentagem inteira. */
    valor: z.number().int().min(1).max(10_000_000).nullable(),
  })
  .refine((t) => (t.tipo === null) === (t.valor === null), "Escolha o tipo e o valor da taxa")
  .refine((t) => t.tipo !== "PERCENTUAL" || (t.valor ?? 0) <= 100, "A porcentagem vai até 100%");
export type TaxaDeUrgenciaInput = z.infer<typeof taxaDeUrgenciaSchema>;
